import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@robocorp/components';

import { useLinkToSlackMutation } from '~/queries';
import { getUrlWithoutQueryString } from '~/utils/removeQsp';

export const OAUTH_SLACK_STATE_LOCALSTORAGE_KEY = 'slack-oauth-state';

export const generateSlackLinkURL = () => {
  const redirectUri = getUrlWithoutQueryString(window.location.href);
  const currentBrowserSessionNonce = window.crypto?.randomUUID?.();

  if (!currentBrowserSessionNonce) {
    // https://caniuse.com/mdn-api_crypto_randomuuid
    // randomUIUID is available for all browsers but IE11

    // eslint-disable-next-line no-alert
    window.alert('Failed to start the Slack Linking process: try using another browser');
    throw new Error('Unable to generate the oauth nonce for Slack linking');
  }

  window.localStorage?.setItem?.(OAUTH_SLACK_STATE_LOCALSTORAGE_KEY, currentBrowserSessionNonce);

  const slackAuthBaseURL = import.meta.env.VITE_SLACK_AUTH_URL;
  const slackAppID = import.meta.env.VITE_SLACK_APP_ID;

  return `${slackAuthBaseURL}?scope=chat:write,channels:read,channels:join&client_id=${slackAppID}&redirect_uri=${redirectUri}&state=${currentBrowserSessionNonce}`;
};

/**
 * Trigger link to Slack mutation when a Slack token is present in the URL (user has succesfully redirected back from Slack.com)
 */
export const useSlackCallback = () => {
  const { mutateAsync: linkToSlack } = useLinkToSlackMutation();
  const { addSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const link = async () => {
      const search = new URLSearchParams(location.search);
      const code = search.get('code');
      const state = search.get('state');

      const existingState = window.localStorage?.getItem?.(OAUTH_SLACK_STATE_LOCALSTORAGE_KEY);
      const stateIsValid = state && state === existingState;
      if (!code || !stateIsValid) return;

      const newLocation = { ...location, search: '' };
      const response = await linkToSlack({ code, state });
      window.localStorage?.removeItem?.(OAUTH_SLACK_STATE_LOCALSTORAGE_KEY);

      if (response?.linked) {
        addSnackbar({
          message: t('slack.succesfullLinking'),
          variant: 'success',
        });
      }

      navigate(newLocation);
    };

    link();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return null;
};
